import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { CHARGE_CATEGORY_OPTIONS, createAdditionalInvoice } from "../../services/additionalInvoiceService";
import { formatMoney } from "../../services/clientPaymentService";
import styles from "./CreateAdditionalInvoiceDialog.module.scss";

const blankCharge = () => ({ label: "", category: "", amount: null });

// Lets an admin bill a new charge (customs duty, a delivery fee correction,
// etc.) on a package whose invoice is already paid, without touching the
// original invoice - see POST /additionalInvoice on the backend.
const CreateAdditionalInvoiceDialog = ({ parentInvoiceId, onHideDialog, onsuccess }) => {
    const dispatch = useDispatch();
    const [charges, setCharges] = useState([blankCharge()]);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const updateCharge = (index, field, value) => {
        setCharges((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
    };

    const addChargeRow = () => setCharges((prev) => [...prev, blankCharge()]);

    const removeChargeRow = (index) => setCharges((prev) => prev.filter((_, i) => i !== index));

    const total = charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    const isValid = charges.length > 0 && charges.every((c) => c.label.trim() && Number(c.amount) > 0);

    const handleSubmit = async () => {
        if (!isValid) {
            toast.warn("Every charge needs a label and an amount greater than $0.");
            return;
        }
        setSubmitting(true);
        const res = await createAdditionalInvoice(dispatch, {
            parentInvoiceId,
            charges: charges.map((c) => ({ label: c.label.trim(), category: c.category || undefined, amount: Number(c.amount) })),
            notes,
        });
        setSubmitting(false);
        if (res?.success && onsuccess) {
            onsuccess();
        }
    };

    return (
        <div className={styles.dialog}>
            <p className={styles.intro}>Bill one or more new charges on this invoice separately - it can be paid independently, without touching the original invoice.</p>

            <div className={styles.chargeList}>
                {charges.map((charge, index) => (
                    <div className={styles.chargeRow} key={index}>
                        <div className={styles.chargeField}>
                            <label>Label</label>
                            <InputText value={charge.label} onChange={(e) => updateCharge(index, "label", e.target.value)} placeholder="e.g. Customs Duty" />
                        </div>
                        <div className={styles.chargeField}>
                            <label>Category</label>
                            <Dropdown value={charge.category} options={CHARGE_CATEGORY_OPTIONS} onChange={(e) => updateCharge(index, "category", e.value)} />
                        </div>
                        <div className={styles.chargeField}>
                            <label>Amount</label>
                            <InputNumber value={charge.amount} onValueChange={(e) => updateCharge(index, "amount", e.value)} mode="currency" currency="USD" locale="en-US" placeholder="$0.00" />
                        </div>
                        <Button icon="pi pi-trash" className={`p-button-text p-button-danger ${styles.removeBtn}`} onClick={() => removeChargeRow(index)} disabled={charges.length === 1} aria-label="Remove charge" />
                    </div>
                ))}
            </div>

            <Button label="Add Charge" icon="pi pi-plus" outlined onClick={addChargeRow} className={styles.addBtn} />

            <div className={styles.field}>
                <label>Notes</label>
                <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Optional context for this additional invoice" />
            </div>

            <div className={styles.totalRow}>
                <span>Total</span>
                <strong>{formatMoney(total)}</strong>
            </div>

            <div className={styles.actions}>
                <Button label="Cancel" className="p-button-text" onClick={onHideDialog} disabled={submitting} />
                <Button label={submitting ? "Creating..." : "Create Additional Invoice"} icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"} onClick={handleSubmit} disabled={submitting || !isValid} />
            </div>
        </div>
    );
};

export default CreateAdditionalInvoiceDialog;
